import api, { apiFormData } from "../../../utils/api";

// ============================================================================
// CRM Leads — frontend service. Talks to the torre-de-control-api leads module.
//   • intake (public)  : POST /leads/intake   (used by the marketing landing)
//   • create (authed)  : POST /leads          (console / API / MCP crm_create_lead)
//   • list   (authed)  : GET  /leadsList
//   • update (authed)  : PUT  /leads?id=
//   • delete (authed)  : DELETE /leads?id=
// The shared `api` axios client injects the auth token + session headers.
// ============================================================================

export type LeadStatus =
  | "new"
  | "contacted"
  | "qualified"
  | "unqualified"
  | "converted"
  | "lost";

/** Product a lead is interested in — set by the landing the lead came from. */
export type LeadProduct = "Coding Cloud" | "Wayak" | "Movik" | "Nuvex";

/** Movik (#1536) es el producto B2C (acompañamiento de trayectos) que comparte plataforma con Wayak (B2B). */
export const LEAD_PRODUCTS: LeadProduct[] = ["Coding Cloud", "Wayak", "Movik", "Nuvex"];

/** Productos B2C: se cierran con la licencia de la app (gratis o de pago), sin cotización. */
export const B2C_PRODUCTS: readonly LeadProduct[] = ["Movik"];

/** ¿El cierre de este producto pasa por una cotización? (igual que productService.requiresQuote de la API). */
export function requiresQuote(product: string | null | undefined): boolean {
  const p = String(product ?? "").trim().toLowerCase();
  return !B2C_PRODUCTS.some((b) => b.toLowerCase() === p);
}

/** Firestore Timestamp as serialized over JSON (admin SDK shape). */
export interface FirestoreTimestamp {
  _seconds?: number;
  _nanoseconds?: number;
  seconds?: number;
  nanoseconds?: number;
}

export type QualificationGrade = "A" | "B" | "C" | "D" | "E";

/** AI grade — written automatically after a call by the LLM (aiCallOrchestrator). */
export interface AiQualification {
  grade: QualificationGrade | null;
  score: number | null;
  disposition: string | null;
  success: boolean | null;
  outcomeLabel: string | null;
  summary: string | null;
  reasoning: string | null;
  callId: string | null;
  callerType: "ai" | "human" | null;
  crmAgentId: string | null;
  qualifiedAt: string | null;
}

/** Human grade — set by the assigned agent via POST /leads/qualify. */
export interface HumanQualification {
  grade: QualificationGrade | null;
  score: number | null;
  success: boolean | null;
  notes: string | null;
  qualifiedBy: string | null;
  qualifiedAt: string | null;
}

/**
 * De qué canal y campaña llegó el lead (#1521). Contrato compartido con la
 * landing y el registro de WAYAK; el backend lo sanea y lo fusiona con la
 * regla «primer toque gana».
 */
export interface LeadAttribution {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  /** Código del enlace corto, ctwa_clid del anuncio o id del anuncio. */
  ref?: string;
  referrer?: string;
  landingPath?: string;
  /** ISO: primer toque. */
  firstSeenAt?: string;
  channel: "landing" | "whatsapp" | "meta_ads" | "qr" | "pos" | "flyer" | "app_store" | "referral" | "other";
}

export interface Lead {
  id: string;
  name: string | null;
  company: string | null;
  email: string | null;
  whatsapp: string | null;
  /** Plain phone (with country code) when the number is NOT a WhatsApp line. The
   *  AI/human caller dials `whatsapp || phone`, so either reaches the lead. */
  phone?: string | null;
  role: string | null;
  system: string | null;
  companySize: string | null;
  problem: string | null;
  contactMethod: string | null;
  /** Service/product the lead is interested in (captured at intake or by the AI call). */
  interestService?: string | null;
  /** How/where the lead heard about us — attribution channel (referido, búsqueda, redes, evento…). */
  acquisitionSource?: string | null;
  /** When acquisitionSource is a referral, who referred them. */
  referralName?: string | null;
  /** Origin of the lead: "landing", "meta_ads", a seed, etc. */
  source: string | null;
  /** Atribución de campaña (#1521); null/ausente en leads anteriores. */
  attribution?: LeadAttribution | null;
  /** Canal de captación: `attribution.channel` o el que implica `source`. */
  channel?: LeadAttribution["channel"] | string | null;
  product: LeadProduct | string | null;
  status: LeadStatus;
  /** Pipeline board this lead sits on (defaults to the "default" board). */
  pipelineId?: string | null;
  /** Column (category) within the pipeline. Mirrors `status` for system columns. */
  categoryId?: string | null;
  /** Set when the lead enters a lost column; powers the 7-day board drop-off. */
  lostAt?: FirestoreTimestamp | string | null;
  score: number;
  ownerId: string | null;
  tags: string[];
  lastContactedAt: string | null;
  /** Dual qualification grades — one by the AI, one by the assigned human agent. */
  aiQualification?: AiQualification | null;
  humanQualification?: HumanQualification | null;
  /** Server-set on leads from the anonymous public intake/booking routes. */
  intakeOrigin?: "public_intake" | null;
  /** Set when the automatic lead.created AI call was skipped (e.g. global hourly budget). */
  aiCall?: { status: "skipped"; reason: string; at: string; capPerHour?: number } | null;
  /** Set on leads imported from Meta Lead Ads — the Graph leadgen_id. */
  metaLeadgenId?: string | null;
  createdAt?: FirestoreTimestamp | string | null;
  updatedAt?: FirestoreTimestamp | string | null;
  meta?: Record<string, unknown>;
}

export interface LeadsPage {
  data: Lead[];
  totalItems: number;
  totalPages: number;
  currentPage: number;
}

interface LeadsListParams {
  page?: number;
  limit?: number;
  status?: LeadStatus;
  product?: LeadProduct;
}

export async function fetchLeads(params: LeadsListParams = {}): Promise<LeadsPage> {
  const { page = 1, limit = 50, status, product } = params;
  const res = await api.get("/leadsList", {
    params: { page, limit, ...(status ? { status } : {}), ...(product ? { product } : {}) },
  });
  // pageResponse shape: { data, meta: { totalItems, totalPages, currentPage } }
  const body = res.data ?? {};
  return {
    data: Array.isArray(body.data) ? body.data : [],
    totalItems: body.meta?.totalItems ?? 0,
    totalPages: body.meta?.totalPages ?? 1,
    currentPage: body.meta?.currentPage ?? page,
  };
}

/** Un lead por id (`GET /leads?id=`); null si no existe. */
export async function fetchLead(id: string): Promise<Lead | null> {
  const res = await api.get("/leads", { params: { id } });
  return res.data?.data ?? null;
}

/** Lo que acepta `POST /leads` desde la consola (requiere correo, WhatsApp o teléfono). */
export interface CreateLeadInput {
  name: string;
  email?: string;
  whatsapp?: string;
  phone?: string;
  company?: string;
  problem?: string;
  source?: string;
  product?: string;
  attribution?: Partial<LeadAttribution> & Pick<LeadAttribution, "channel">;
  /** Bandeja de chat (#1524): liga la conversación al lead nuevo y omite la llamada automática. */
  conversationId?: string;
}

/** Crea un lead desde la consola (`POST /leads`). */
export async function createLead(input: CreateLeadInput): Promise<Lead> {
  const res = await api.post("/leads", input);
  return res.data?.data;
}

export async function updateLead(
  id: string,
  patch: Partial<Pick<Lead, "status" | "pipelineId" | "categoryId" | "product" | "ownerId" | "score" | "tags" | "name" | "email" | "whatsapp" | "company" | "role" | "system" | "companySize" | "problem" | "contactMethod" | "interestService" | "acquisitionSource" | "referralName" | "lastContactedAt" | "attribution">>,
): Promise<Lead> {
  const res = await api.put(`/leads`, patch, { params: { id } });
  return res.data?.data;
}

export async function deleteLead(id: string): Promise<void> {
  await api.delete(`/leads`, { params: { id } });
}

/** Set the assigned human agent's qualification grade for a lead. */
export async function qualifyLeadHuman(
  id: string,
  input: { grade?: QualificationGrade | null; score?: number | null; success?: boolean | null; notes?: string | null },
): Promise<Lead> {
  const res = await api.post(`/leads/qualify`, input, { params: { id } });
  return res.data?.data;
}

/** Summary returned by the bulk import endpoint. */
export interface BulkImportResult {
  total: number;
  created: number;
  skipped: number;
  errors: { row: number | null; reason: string }[];
}

/**
 * Upload an Excel/CSV file of contacts for bulk creation. Each valid row becomes
 * a `new` lead flagged as an import (so it does NOT trigger an automated call),
 * and gets a Pipeline card via the backend trigger.
 */
export async function bulkImportLeads(file: File): Promise<BulkImportResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFormData.post("/leads/bulk", form);
  return (
    res.data?.data ?? { total: 0, created: 0, skipped: 0, errors: [] }
  );
}

/** Convert a Firestore timestamp / ISO string into a JS Date (or null). */
export function leadDate(
  value: FirestoreTimestamp | string | null | undefined,
): Date | null {
  if (!value) return null;
  if (typeof value === "string") {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  const seconds = value._seconds ?? value.seconds;
  if (typeof seconds === "number") return new Date(seconds * 1000);
  return null;
}
